/**
 * Liste de courses — façade de Home Assistant
 *
 * Deux files d'attente se relaient derrière cet écran, et l'utilisateur ne doit
 * en voir qu'une chose : que son geste est pris en compte.
 *
 *   téléphone ──(file du client HTTP)──▶ Foyer ──(courses_outbox)──▶ Home Assistant
 *
 * La première franchit une coupure de réseau mobile, la seconde une coupure entre
 * le serveur et HA. D'où l'indicateur « en attente » sur un article : il ne veut
 * pas dire « raté », il veut dire « pas encore arrivé au bout ». Le distinguer
 * d'un échec est tout l'intérêt.
 *
 * Le cochage est **optimiste** : la ligne change d'état avant la réponse. Un
 * article coché qui met deux secondes à s'afficher donne l'impression d'une
 * application cassée, alors qu'on est debout dans un rayon.
 */

import { useState } from 'react';
import { Check, Cloud, CloudOff, Plus, RefreshCw, ShoppingCart, Trash2, TriangleAlert } from 'lucide-react';

import { Chargement, Erreur, TRAIT, Vide } from '../composants/ui.js';
import { MiseEnFile } from '../lib/api.js';
import { navigue, useAppli, useRessource } from '../lib/appli.js';
import { donnees } from '../lib/donnees.js';

interface Article {
  nom: string;
  coche: boolean;
  rayon: string | null;
  source: string | null;
  etat: 'synchronise' | 'en_attente';
}

export function Courses() {
  const { rafraichis } = useAppli();
  const liste = useRessource(() => donnees.courses());

  const [saisie, setSaisie] = useState('');
  const [occupe, setOccupe] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [divergences, setDivergences] = useState<{ nom: string; explication: string }[]>([]);
  const [retires, setRetires] = useState<string[]>([]);
  /** Cochages appliqués localement en attendant la réponse du serveur. */
  const [optimiste, setOptimiste] = useState<Record<string, boolean>>({});

  if (liste.chargement && !liste.donnee) return <Chargement quoi="Chargement de la liste" />;
  if (liste.erreur && !liste.donnee) return <Erreur message={liste.erreur} />;
  const etat = liste.donnee!;

  const articles: Article[] = etat.articles.map((a) => ({
    ...a,
    coche: a.nom in optimiste ? optimiste[a.nom]! : a.coche,
    etat: a.nom in optimiste ? 'en_attente' : a.etat,
  }));

  async function agis(action: () => Promise<unknown>, apres?: () => void) {
    setOccupe(true);
    setMessage(null);
    try {
      await action();
      apres?.();
      liste.recharge();
      rafraichis();
    } catch (e) {
      // Une mise en file n'est pas une erreur : c'est le comportement voulu
      // hors ligne, et le dire comme un échec inquiéterait pour rien.
      if (e instanceof MiseEnFile) {
        setMessage('Hors ligne — l’opération partira dès le retour du réseau.');
        rafraichis();
      } else {
        setMessage(e instanceof Error ? e.message : 'Opération impossible');
      }
    } finally {
      setOccupe(false);
    }
  }

  async function coche(a: Article) {
    const voulu = !a.coche;
    setOptimiste((o) => ({ ...o, [a.nom]: voulu }));
    await agis(
      () => donnees.cocheCourse(a.nom, voulu),
      () => {
        setOptimiste((o) => {
          const { [a.nom]: _, ...reste } = o;
          return reste;
        });
      },
    );
  }

  async function synchronise() {
    setOccupe(true);
    setMessage(null);
    try {
      const r = await donnees.synchroniseCourses();
      setDivergences(r.divergences.map((d) => ({ nom: d.nom, explication: d.explication })));
      setRetires(r.retires);
      if (r.vidage.bloquee) setMessage(`Envoi interrompu : ${r.vidage.bloquee}`);
      else if (r.vidage.abandonnees.length > 0) {
        setMessage(
          `Abandonné : ${r.vidage.abandonnees.map((x) => `${x.nom} (${x.raison})`).join(', ')}`,
        );
      }
      setOptimiste({});
      liste.recharge();
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Synchronisation impossible');
    } finally {
      setOccupe(false);
    }
  }

  const aAcheter = articles.filter((a) => !a.coche);
  const prises = articles.filter((a) => a.coche);

  return (
    <>
      <div className="entete-vue">
        <h1>
          Courses
        </h1>
        <button
          type="button"
          className="bouton icone"
          onClick={synchronise}
          disabled={occupe || !etat.configure}
          aria-label="Synchroniser avec Home Assistant"
          title={etat.configure ? 'Synchroniser' : 'Home Assistant n’est pas configuré'}
        >
          <RefreshCw size={20} strokeWidth={TRAIT} />
        </button>
      </div>

      <p className="chapeau">
        La liste vit dans Home Assistant. Foyer l’affiche et y ajoute ce que les tâches d’entretien
        réclament.
      </p>

      {!etat.configure && (
        <div className="note">
          <Cloud size={18} strokeWidth={TRAIT} />
          <div>
            <strong>Home Assistant n’est pas relié.</strong>
            <p>
              La liste fonctionne quand même en local, mais rien ne partira vers votre liste
              domestique.{' '}
              <button type="button" className="bouton texte" onClick={() => navigue('/integrations')}>
                Configurer Home Assistant
              </button>
            </p>
          </div>
        </div>
      )}

      {/* Conditionné à `configure` : sans Home Assistant, il n'y a rien à
          transmettre, et annoncer « pas encore transmis » à chaque article
          laisserait croire à une panne alors que la liste locale marche. */}
      {etat.configure && etat.enAttente > 0 && (
        <div className="note">
          <CloudOff size={18} strokeWidth={TRAIT} />
          <span>
            {etat.enAttente === 1
              ? '1 modification pas encore transmise à Home Assistant.'
              : `${etat.enAttente} modifications pas encore transmises à Home Assistant.`}
          </span>
        </div>
      )}

      {divergences.length > 0 && (
        <div className="alerte">
          <TriangleAlert size={18} strokeWidth={TRAIT} />
          <div>
            <strong>Désaccord avec Home Assistant</strong>
            {divergences.map((d) => (
              <p key={d.nom}>
                <strong>{d.nom}</strong> — {d.explication}
              </p>
            ))}
          </div>
        </div>
      )}

      {retires.length > 0 && (
        <div className="note">
          <span>
            Retiré depuis Home Assistant : {retires.join(', ')}. Ce n’est pas une erreur — quelqu’un
            l’a supprimé là-bas.
          </span>
        </div>
      )}

      <form
        className="ligne-ajout"
        onSubmit={(e) => {
          e.preventDefault();
          const nom = saisie.trim();
          if (!nom) return;
          void agis(() => donnees.ajouteCourse(nom), () => setSaisie(''));
        }}
      >
        <input
          type="text"
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
          placeholder="Ajouter un article"
          aria-label="Nom de l’article à ajouter"
          maxLength={120}
        />
        <button type="submit" className="bouton" disabled={occupe || !saisie.trim()}>
          <Plus size={18} strokeWidth={TRAIT} />
          Ajouter
        </button>
      </form>

      {message && <p className="note">{message}</p>}

      {articles.length === 0 ? (
        <Vide
          titre="Rien à acheter"
          texte="Ajoutez un article, ou cochez une tâche d’entretien qui réclame un consommable."
        />
      ) : (
        <>
          <ul className="liste-courses">
            {aAcheter.map((a) => (
              <LigneArticle key={a.nom} article={a} occupe={occupe} onCoche={coche} onRetire={(n) => agis(() => donnees.retireCourse(n))} />
            ))}
          </ul>

          {prises.length > 0 && (
            <>
              <h2 className="section-titre">Déjà pris ({prises.length})</h2>
              <ul className="liste-courses">
                {prises.map((a) => (
                  <LigneArticle key={a.nom} article={a} occupe={occupe} onCoche={coche} onRetire={(n) => agis(() => donnees.retireCourse(n))} />
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </>
  );
}

function LigneArticle({
  article,
  occupe,
  onCoche,
  onRetire,
}: {
  article: Article;
  occupe: boolean;
  onCoche(a: Article): void;
  onRetire(nom: string): void;
}) {
  return (
    <li className={article.coche ? 'article-course pris' : 'article-course'}>
      <button
        type="button"
        className="cocher"
        onClick={() => onCoche(article)}
        aria-label={article.coche ? `Décocher ${article.nom}` : `Cocher ${article.nom}`}
        aria-pressed={article.coche}
      >
        {article.coche && <Check size={16} strokeWidth={3} />}
      </button>

      <div className="corps-article">
        <span className="nom-article">{article.nom}</span>
        {article.source && <span className="source-article">{article.source}</span>}
        {article.rayon && <span className="rayon-article">{article.rayon}</span>}
      </div>

      {article.etat === 'en_attente' && (
        <span className="pastille-attente" title="Pas encore transmis à Home Assistant">
          <CloudOff size={14} strokeWidth={TRAIT} />
        </span>
      )}

      <button
        type="button"
        className="bouton texte"
        onClick={() => onRetire(article.nom)}
        disabled={occupe}
        aria-label={`Retirer ${article.nom}`}
      >
        <Trash2 size={16} strokeWidth={TRAIT} />
      </button>
    </li>
  );
}

/** Icône exportée pour la barre de navigation. */
export const IconeCourses = ShoppingCart;
